// src/components/DCS/DCSModeSelect.tsx
import { useEffect, useMemo, useState } from 'react';
import { Container, Row, Col, Card, Badge, Button, Alert, Spinner } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import { client } from '../../lib/AmplifyClient';
import type { Schema } from '../../../amplify/data/resource';
import { useUserContext } from '../Context/UserContext';

type Profile = Schema['Profile']['type'];
type Issue   = Schema['Issue']['type'];

const DCS_MODES = ['competing', 'collaborating', 'compromising', 'avoiding', 'accommodating'] as const;
type DcsMode = (typeof DCS_MODES)[number];

const MODES: Array<{ key: DcsMode; title: string; desc: string; badge: 'success' | 'danger' | 'info' | 'warning' | 'secondary' }> = [
  { key: 'avoiding',       title: 'Avoiding',       desc: 'Delays/defers conflict; low assertiveness & cooperativeness.',        badge: 'secondary' },
  { key: 'competing',      title: 'Competing',      desc: 'Win/lose decisiveness; high assertiveness, low cooperativeness.',     badge: 'danger' },
  { key: 'accommodating',  title: 'Accommodating',  desc: 'Yields to maintain harmony; low assertiveness, high cooperativeness.',badge: 'info' },
  { key: 'collaborating',  title: 'Collaborating',  desc: 'Win/win integration; high assertiveness & cooperativeness.',          badge: 'success' },
  { key: 'compromising',   title: 'Compromising',   desc: 'Split the difference; moderate assertiveness & cooperativeness.',     badge: 'warning' },
];

// ---------- helpers ----------
const mustHaveEmail = (email?: string | null) => {
  if (!email) throw new Error('No email on user. Please add an email to your profile.');
};
const toLowerEmail = (v?: string | null) => (v ?? '').trim().toLowerCase() || null;

type GqlLike<T> = { data?: T | null; errors?: Array<{ message?: string }> };
const unwrap = <T,>(label: string, res: any): T | null => {
  if (res && typeof res === 'object' && 'data' in res) {
    const { data, errors } = res as GqlLike<T>;
    if (errors?.length) {
      console.error(`[DCSModeSelect][${label}][ERRORS]`, errors);
      return null;
    }
    return (data ?? null) as T | null;
  }
  return (res as T) ?? null;
};

// -------- Profile I/O (identifier = emailLower) --------
async function readProfileByEmailLower(email?: string | null): Promise<Profile | null> {
  const e = toLowerEmail(email);
  if (!e) return null;
  try {
    const r1 = await (client as any).models.Profile.get({ emailLower: e }, { authMode: 'userPool' });
    const v1 = unwrap<Profile>('READ Profile.get by emailLower (userPool)', r1);
    if (v1) return v1;
  } catch {}
  const r2 = await (client as any).models.Profile.get({ emailLower: e }, { authMode: 'apiKey' });
  return unwrap<Profile>('READ Profile.get by emailLower (apiKey)', r2);
}

async function findProfileBySub(sub?: string | null): Promise<Profile | null> {
  if (!sub) return null;
  try {
    const r1 = await (client as any).models.Profile.list(
      { filter: { sub: { eq: sub } }, limit: 1 },
      { authMode: 'userPool' }
    );
    const arr1 = unwrap<Profile[]>('READ Profile.list by sub (userPool)', r1) || [];
    if (arr1 && arr1[0]) return arr1[0];
  } catch {}
  const r2 = await (client as any).models.Profile.list(
    { filter: { sub: { eq: sub } }, limit: 1 },
    { authMode: 'apiKey' }
  );
    const arr2 = unwrap<Profile[]>('READ Profile.list by sub (apiKey)', r2) || [];
  return (arr2 && arr2[0]) || null;
}

/** Create only with userPool (apiKey cannot write) */
async function createProfileIfMissing(email: string, sub?: string | null): Promise<Profile> {
  const low = toLowerEmail(email)!;
  const existing = await readProfileByEmailLower(low);
  if (existing) return existing;

  const input = { emailLower: low, email, ...(sub ? { sub } : {}) };
  const r = await (client as any).models.Profile.create(input, { authMode: 'userPool' });
  const created = unwrap<Profile>('WRITE Profile.create (userPool)', r);
  if (!created) throw new Error('Create failed for Profile. Check auth rules & identifier.');
  return created;
}

/** Update only with userPool (apiKey cannot write) */
async function updateProfileMode(email: string, mode: DcsMode): Promise<Profile | null> {
  const low = toLowerEmail(email)!;
  const r = await (client as any).models.Profile.update(
    { emailLower: low, dcsMode: mode },
    { authMode: 'userPool' }
  );
  return unwrap<Profile>('WRITE Profile.update dcsMode (userPool)', r);
}

// -------- Issue snapshot mirroring --------
async function getIssue(id: string): Promise<Issue | null> {
  try {
    const r1 = await (client as any).models.Issue.get({ id }, { authMode: 'userPool' });
    const v1 = unwrap<Issue>('READ Issue.get (userPool)', r1);
    if (v1) return v1;
  } catch {}
  const r2 = await (client as any).models.Issue.get({ id }, { authMode: 'apiKey' });
  return unwrap<Issue>('READ Issue.get (apiKey)', r2);
}

async function updateIssue(id: string, patch: Partial<Issue>): Promise<Issue | null> {
  const r = await (client as any).models.Issue.update({ id, ...patch }, { authMode: 'userPool' });
  return unwrap<Issue>('WRITE Issue.update (userPool)', r);
}

/** Mirror Profile.dcsMode into Issue.{initiatorDCS|collaboratorDCS} */
async function ensureIssueSnapshot(sessionId: string, userEmail: string, pickedMode: string) {
  const id = sessionId.trim();
  const email = toLowerEmail(userEmail);
  if (!id || !email || !pickedMode) return;

  const issue = await getIssue(id);
  if (!issue) return;

  const initiatorEmail = String((issue as any)?.initiatorEmail ?? '').trim().toLowerCase();
  const collaboratorEmail = String((issue as any)?.collaboratorEmail ?? '').trim().toLowerCase();
  const amInitiator = email === initiatorEmail;
  const amCollaborator = email === collaboratorEmail;

  if (!amInitiator && !amCollaborator) {
    console.warn('[DCSModeSelect] User email does not match initiator/collaborator on Issue; skipping snapshot.');
    return;
  }

  const currentInitiator = (issue as any)?.initiatorDCS ?? null;
  const currentCollaborator = (issue as any)?.collaboratorDCS ?? null;

  if (amInitiator && currentInitiator !== pickedMode) {
    console.log('[DCSModeSelect] Writing initiatorDCS:', pickedMode);
    await updateIssue(id, { initiatorDCS: pickedMode } as any);
  }
  if (amCollaborator && currentCollaborator !== pickedMode) {
    console.log('[DCSModeSelect] Writing collaboratorDCS:', pickedMode);
    await updateIssue(id, { collaboratorDCS: pickedMode } as any);
  }
}

/** Decide where to go next based on Issue snapshot */
async function decideNextRoute(sessionId: string): Promise<'propose' | 'waiting'> {
  const fresh = await getIssue(sessionId);
  const i = (fresh as any) || {};
  const initiatorDCS = (i.initiatorDCS ?? '').toString().trim();
  const collaboratorDCS = (i.collaboratorDCS ?? '').toString().trim();
  const both = Boolean(initiatorDCS && collaboratorDCS);

  console.log('[DCSModeSelect][DECIDE]',
    { initiatorDCS, collaboratorDCS, both });

  return both ? 'propose' : 'waiting';
}

// ---------- component ----------
export default function DCSModeSelect() {
  const { id } = useParams(); // session id
  const navigate = useNavigate();
  const { sub, email, firstName } = useUserContext();

  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState<string | null>(null);
  const [selected, setSelected] = useState<DcsMode | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved]   = useState(false);

  const displayName = useMemo(() => (firstName || email || 'there') as string, [firstName, email]);

  // Load existing profile; if it already has a mode, preselect, backfill snapshot, then route based on BOTH snapshots
  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        mustHaveEmail(email);
        const byEmail = await readProfileByEmailLower(email!);
        const profile =
          byEmail ?? (await findProfileBySub(sub ?? null));

        if (!mounted) return;

        if (profile) {
          const existing = (profile?.dcsMode?.toString().trim() || '') as DcsMode | '';
          if (existing && (DCS_MODES as readonly string[]).includes(existing)) {
            setSelected(existing as DcsMode);
            setSaved(true);

            if (id && email) {
              await ensureIssueSnapshot(id, email, existing);
              const next = await decideNextRoute(id);
              if (next === 'propose') {
                console.log('[DCSModeSelect][NAV] existing dcsMode + both snapshots → propose-strategies');
                navigate(`/session/${encodeURIComponent(id)}/propose-strategies`);
              } else {
                console.log('[DCSModeSelect][NAV] existing dcsMode but other missing → prepare-strategies-waiting');
                navigate(`/session/${encodeURIComponent(id)}/propose-strategies`);
              }
              return;
            }
          }
        }
      } catch (e: any) {
        if (!mounted) return;
        setError(e?.message || 'Failed to load your profile.');
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [sub, id, navigate, email]);

  const handleSave = async () => {
    try {
      mustHaveEmail(email);
      if (!selected) return;

      setSaving(true);
      setError(null);

      // 1) ensure profile exists keyed by emailLower, then update its dcsMode
      await createProfileIfMissing(email!, sub ?? null);
      const updated = await updateProfileMode(email!, selected);
      if (!updated) throw new Error('Profile update returned null. Check Profile auth rules.');

      // 2) verify read
      const rb = await readProfileByEmailLower(email!);
      const ok = (rb?.dcsMode?.toString().trim() || '') === selected;
      setSaved(ok);
      if (!ok) throw new Error('Saved, but verification failed. Try again.');

      // 3) mirror to Issue snapshot for this session
      if (id && email) {
        await ensureIssueSnapshot(id, email, selected);

        // 4) route ONLY if both snapshots present
        const next = await decideNextRoute(id);
        if (next === 'propose') {
          console.log('[DCSModeSelect][NAV] saved dcsMode (+snapshot) both present → propose-strategies');
          navigate(`/session/${encodeURIComponent(id)}/propose-strategies`);
        } else {
          console.log('[DCSModeSelect][NAV] saved dcsMode but other missing → prepare-strategies-waiting');
          navigate(`/session/${encodeURIComponent(id)}/propose-strategies`);
        }
      }
    } catch (e: any) {
      const msg = e?.errors?.[0]?.message || e?.message || String(e);
      console.error('[DCSModeSelect][ERROR]', msg);
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  const skyBlueActive = '#5ec0ea';
  const slate = '#eceff1';
  const darkBlue = '#0d47a1';

  if (loading) {
    return (
      <Container className="py-4 text-center">
        <Spinner animation="border" role="status" />
        <div className="mt-3">Loading your profile…</div>
      </Container>
    );
  }

  return (
    <Container className="py-4">
      <Row className="mb-3">
        <Col>
          <h3 className="mb-1">Choose Your Conflict Mode</h3>
          <div className="text-muted">
            {`Hi ${displayName}, pick the style you typically use during conflict.`}
            {id ? `  (Session: ${id})` : null}
          </div>
        </Col>
      </Row>

      {error && (
        <Row className="mb-3">
          <Col><Alert variant="danger">{error}</Alert></Col>
        </Row>
      )}

      <Row xs={1} md={2} lg={3} className="g-3">
        {MODES.map((m) => {
          const isActive = selected === m.key;
          return (
            <Col key={m.key}>
              <Card
                className="h-100 hover-shadow-sm"
                style={{
                  background: slate,
                  borderColor: isActive ? skyBlueActive : 'transparent',
                  boxShadow: isActive ? '0 0 0 3px rgba(94,192,234,0.4)' : undefined,
                  // keep full interactivity even after saved
                  cursor: 'pointer',
                }}
                onClick={() => { setSelected(m.key); setSaved(false); }}
              >
                <Card.Body>
                  <Card.Title className="d-flex align-items-center justify-content-between">
                    {m.title}
                    <Badge bg={m.badge}>{m.title}</Badge>
                  </Card.Title>
                  <Card.Text className="text-muted mb-0">{m.desc}</Card.Text>
                </Card.Body>
              </Card>
            </Col>
          );
        })}
      </Row>

      <Row className="mt-3">
        <Col className="d-flex justify-content-end">
          <Button
            onClick={handleSave}
            disabled={!selected || saving}  // <-- stays enabled after saved
            style={{ backgroundColor: darkBlue, borderColor: darkBlue, paddingLeft: '1.25rem', paddingRight: '1.25rem' }}
          >
            {saving ? 'Saving…' : saved ? 'Save Again' : 'Save'}
          </Button>
        </Col>
      </Row>

      {/* Google-blue Home button */}
      <Row className="mt-4">
        <Col className="d-flex justify-content-center">
          <Button
            onClick={() => navigate('/')}
            style={{
              backgroundColor: '#4285F4', // Google blue
              borderColor: '#4285F4',
              paddingLeft: '1.5rem',
              paddingRight: '1.5rem',
            }}
          >
            Home
          </Button>
        </Col>
      </Row>
    </Container>
  );
}
